// ----------------------------------------------------------------------
// Icon registry.
//
// CONVENTION: Phosphor components imported from `@phosphor-icons/react`.
// Weight is a prop, not a name suffix — `<Iconify>` defaults it to "light", so
// entries here are the bare component. Pass `weight` at the call site to override.
//
// Because these are real imports, a misspelled icon is a compile error and Vite
// tree-shakes the bundle down to what is referenced. That is why there is no icon
// build step: `tsc` is the validator.
//
// Until Aug 2026 this file pulled from 56 different collections (tabler,
// heroicons, mdi, fluent, solar, eva, …) with incompatible stroke weights and
// optical grids. One set, one weight.
//
// Do not import a Phosphor icon at a call site — add it here and reference
// `icons.<category>.<key>`. Scattered literals are what produced the mosaic.
//
// THE 54 STRING VALUES BELOW ARE DELIBERATE — do not "fix" them to Phosphor.
// They stay Iconify names, resolved at runtime by @iconify/react:
//   • publishers — brand marks, and brands own their colors. Phosphor has 10 of
//     these as monochrome outlines and simply lacks the other 10 (TripAdvisor,
//     Bing, Yelp, Foursquare, Waze, Swarm, Judysbook, Tumblr, Zillow).
//   • commons.loading — an animated SVG. `Spinner` is static and would
//     need a CSS rotation.
//   • commons.thumbsUp / thumbsDown — colored emoji in the AI chat feedback.
//   • letters — Phosphor has no alphabetic glyphs. These are the week/month/year
//     filters in social/components/posts/FilterDrawer.tsx.
//   • platformGlyphs / chatbot / trend / legacyComposer / settingsDrawer — moved in
//     from call sites in Aug 2026 keeping their original names, so that de-scattering
//     the literals carried no visual change. See the note above those groups.
// Country flags are not here: they live in libs/translation/availableLanguages.ts.
// ----------------------------------------------------------------------

import {
  ArrowCircleDown,
  ArrowCircleUp,
  ArrowCounterClockwise,
  ArrowDown,
  ArrowLeft,
  ArrowLineLeft,
  ArrowLineRight,
  ArrowRight,
  ArrowSquareOut,
  ArrowSquareUpRight,
  ArrowUp,
  ArrowUpRight,
  ArrowsClockwise,
  ArrowsDownUp,
  ArrowsInSimple,
  Article,
  ArticleNyTimes,
  At,
  Barbell,
  Basket,
  Bed,
  Bell,
  Bicycle,
  Bone,
  BookmarkSimple,
  Books,
  BoxingGlove,
  Brain,
  Briefcase,
  Broom,
  BugBeetle,
  Buildings,
  Butterfly,
  Cake,
  CalendarBlank,
  CalendarDots,
  CallBell,
  Camera,
  Car,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Cat,
  ChartLine,
  ChartLineUp,
  ChatCircle,
  ChatText,
  ChatsCircle,
  Check,
  CheckCircle,
  Checks,
  ClipboardText,
  Clock,
  ClockCounterClockwise,
  Coffee,
  Compass,
  Confetti,
  Copy,
  DeviceMobile,
  Devices,
  DiscoBall,
  Dog,
  Door,
  Dot,
  DotsSixVertical,
  DotsThree,
  DotsThreeVertical,
  DownloadSimple,
  Dress,
  Drop,
  EnvelopeSimple,
  Equals,
  Export,
  Eye,
  EyeSlash,
  Eyeglasses,
  FacebookLogo,
  Fan,
  File,
  FileDashed,
  Files,
  FilmStrip,
  FloppyDisk,
  FlowerLotus,
  FlowerTulip,
  ForkKnife,
  Funnel,
  Gear,
  GearSix,
  Gift,
  GlobeSimple,
  GoogleLogo,
  HairDryer,
  Hamburger,
  HandSoap,
  Handbag,
  Handshake,
  Hash,
  Heart,
  HighlighterCircle,
  Hospital,
  House,
  ImageSquare,
  Images,
  ImagesSquare,
  Info,
  InstagramLogo,
  Link,
  LinkBreak,
  LinkedinLogo,
  List,
  ListStar,
  MagnifyingGlass,
  MapPin,
  MapPinArea,
  Martini,
  Megaphone,
  Microscope,
  NotePencil,
  PaintBrush,
  PaperPlaneRight,
  PaperPlaneTilt,
  Paperclip,
  Pause,
  PawPrint,
  PenNib,
  Pencil,
  PencilLine,
  PencilSimple,
  Percent,
  Person,
  PersonSimpleHike,
  Phone,
  Pill,
  PinterestLogo,
  Pipe,
  Play,
  PlayCircle,
  PlugCharging,
  Plus,
  PlusSquare,
  PottedPlant,
  QrCode,
  Question,
  RedditLogo,
  Robot,
  RocketLaunch,
  Scissors,
  ShareNetwork,
  ShieldCheck,
  ShoppingCartSimple,
  SignOut,
  SketchLogo,
  Smiley,
  SmileySad,
  SmileyWink,
  SneakerMove,
  SolarRoof,
  Sparkle,
  SpeakerHigh,
  SpeakerSlash,
  Spinner,
  SprayBottle,
  SquaresFour,
  StarFour,
  Stethoscope,
  StopCircle,
  Storefront,
  SuitcaseSimple,
  Syringe,
  TextAlignLeft,
  ThreadsLogo,
  ThumbsDown,
  ThumbsUp,
  TiktokLogo,
  Tire,
  Toolbox,
  Tooth,
  Translate,
  Trash,
  TrendDown,
  TrendUp,
  Truck,
  TruckTrailer,
  UploadSimple,
  User,
  UserCircle,
  UserCirclePlus,
  Users,
  UsersFour,
  UsersThree,
  VideoCamera,
  Warning,
  WarningCircle,
  Wrench,
  X,
  XCircle,
  XLogo,
  YoutubeLogo,
} from '@phosphor-icons/react';

const icons = {
  navbar: {
    listing: Compass,
    reputation: Megaphone,
    jooiceAi: Sparkle,
    chat: ChatsCircle,
    agents: Robot,
    help: Question,
    orgSettings: Buildings,
    avatar: UserCircle,
    collapse: ArrowsInSimple,
    install: DeviceMobile,
    apps: SquaresFour,
    social: UsersThree,
    insights: TrendUp,
    house: House,
    listStar: ListStar,
    notePencil: NotePencil,
    mapPinArea: MapPinArea,
    settings: GearSix,
  },

  locations: {
    mapPin: MapPin,
  },

  arrows: {
    up: ArrowUp,
    upRight: ArrowUpRight,
    upRightSquared: ArrowSquareUpRight,
    down: ArrowDown,
    right: ArrowRight,
    left: ArrowLeft,
    start: ArrowLineLeft,
    end: ArrowLineRight,
    leftBold: CaretLeft,
    rightBold: CaretRight,
    upBold: CaretUp,
    downBold: CaretDown,
    trendUp: TrendUp,
    trendDown: TrendDown,
    // Phosphor has no flat-trend arrow; `equals` is the conventional "unchanged"
    // marker and reads correctly beside trendUp / trendDown.
    trendStable: Equals,
    downCircle: ArrowCircleDown,
    upCircle: ArrowCircleUp,
  },

  commons: {
    avatar: UserCircle,
    carousel: ImagesSquare,
    unlink: LinkBreak,
    link: Link,
    smile: Smiley,
    sad: SmileySad,
    sort: ArrowsDownUp,
    loading: 'svg-spinners:tadpole', // EXCEPTION: animated
    edit: PencilSimple,
    plus: Plus,
    cross: X,
    burger: List,
    logout: SignOut,
    filter: Funnel,
    search: MagnifyingGlass,
    sync: ArrowsClockwise,
    clipboardEdit: ClipboardText,
    error: WarningCircle,
    trash: Trash,
    copy: Copy,
    // The approved state of the copy-and-approve action: what it signals is
    // "approved", not "copy", hence a plain check.
    'copy-check': Check,
    reset: ArrowCounterClockwise,
    dots: DotsThree,
    user: User,
    phone: Phone,
    briefcase: Briefcase,
    briefcase2: Briefcase, // now identical to `briefcase`; candidate for removal
    building: Buildings,
    email: EnvelopeSimple,
    send: PaperPlaneTilt,
    sendRight: PaperPlaneRight,
    megaphone: Megaphone,
    openExternal: ArrowSquareOut,
    stars: StarFour,
    upload: UploadSimple,
    attachment: Paperclip,
    warning: WarningCircle,
    warningTriangle: Warning,
    checked: CheckCircle,
    closeCircle: XCircle,
    rocket: RocketLaunch,
    translate: Translate,
    dotsHorizontal: DotsThree,
    settings: Gear,
    doubleCheck: Checks,
    verticalDots: DotsThreeVertical,
    history: ClockCounterClockwise,
    // Phosphor has no chat-plus; the button this sits on is labelled "new chat",
    // so the icon carries the action and the label carries the object.
    addChat: Plus,
    close: X,
    paperPlane: PaperPlaneTilt,
    delete: Trash,
    pencil: Pencil,
    sender: User,
    ai: Sparkle,
    closedEye: EyeSlash,
    check: Check,
    calendar: CalendarBlank,
    hashtag: Hash,
    emoji: Smiley,
    newPost: NotePencil,
    document: Article,
    reel: FilmStrip,
    imageSquare: ImageSquare,
    camera: Camera,
    clock: Clock,
    clockSmall: Clock,
    play: PlayCircle,
    trendingUp: TrendUp,
    arrowUp: ArrowUp,
    arrowDown: ArrowDown,
    infoFilled: Info,
    info: Info,
    errorCircle: WarningCircle,
    question: Question,
    shop: Storefront,
    'shopping-cart': ShoppingCartSimple,

    // AI reply tones. Keys must stay aligned with EJooiceAiReviewTone's values —
    // ReviewResponseField indexes this object with the enum, so a rename here is a
    // runtime break that TypeScript reports only as an index-signature error.
    // Chosen to be mutually distinguishable at 20px in the tone toggle group.
    friendly: SmileyWink,
    formal: SuitcaseSimple,
    concise: TextAlignLeft,
    humorous: Confetti,
    infoCircle: Info,
    infoCircleSolid: Info,
    thumbsUp: 'fluent-emoji:thumbs-up', // EXCEPTION: colored emoji
    thumbsDown: 'fluent-emoji:thumbs-down', // EXCEPTION: colored emoji
    checkCircle: CheckCircle,
    fillArrowUp: CaretUp,
    fillArrowDown: CaretDown,
    gear: Gear,
    dot: Dot,
    at: At,
    pictures: Images,
    bell: Bell,
    message: ChatCircle,
    visibility: Eye,
    qrCode: QrCode,
    sms: ChatText,
    download: DownloadSimple,
    analytics: ChartLine,
    gate: Door,
    eye: Eye,
    draft: PencilLine,
    stop: StopCircle,
    eyeSearch: Eye,
    refresh: ArrowsClockwise,
    pictureStock: ImagesSquare,
    drag: DotsSixVertical,
    hide: EyeSlash,
    video: VideoCamera,
    file: File,
    imageCredits: Images,
    textCredits: Article,

    // Added Aug 2026 when the last raw literals moved in here from the Spartacus
    // onboarding option grids. They were regular weight at those call sites and are
    // light now, like everything else.
    userCirclePlus: UserCirclePlus,
    calendarDots: CalendarDots,
    press: ArticleNyTimes,
    eyeglasses: Eyeglasses,
    users: Users,
    usersThree: UsersThree,
    usersFour: UsersFour,
  },

  // Social planner editor (the `_`-prefixed module). These were regular- and
  // fill-weight literals at their call sites until Aug 2026; folded into the light
  // convention with everything else, so play/pause are now outlines, not solids.
  editor: {
    spinner: Spinner,
    save: FloppyDisk,
    share: ShareNetwork,
    play: Play,
    pause: Pause,
    volumeOn: SpeakerHigh,
    volumeOff: SpeakerSlash,
    draftFile: FileDashed,
  },

  socialPreview: {
    thumbsUp: ThumbsUp,
    thumbsDown: ThumbsDown,
    arrowSquare: ArrowsClockwise,
    upload: UploadSimple,
    like: Heart,
    comment: ChatCircle,
    share: PaperPlaneTilt,
    save: BookmarkSimple,
  },

  iosIcons: {
    share: Export,
    add: PlusSquare,
  },

  // Monochrome brand glyphs for inline use next to text (metric rows, labels), where a
  // colored mark would fight the surrounding type. These DO follow the Phosphor light
  // convention — Phosphor ships outline logos — unlike `publishers` below, which are the
  // full-color marks used on publisher/connection cards. Keys are lowercased platform
  // names because callers index this with `platform.toLowerCase()`.
  socialPlatforms: {
    bluesky: Butterfly, // Phosphor has no Bluesky mark; theirs is a butterfly
    facebook: FacebookLogo,
    google: GoogleLogo,
    instagram: InstagramLogo,
    linkedin: LinkedinLogo,
    pinterest: PinterestLogo,
    reddit: RedditLogo,
    threads: ThreadsLogo,
    tiktok: TiktokLogo,
    twitter: XLogo,
    youtube: YoutubeLogo,
  },

  // EXCEPTION — brand marks. Brands own their colors; Phosphor lacks half of these.
  publishers: {
    facebook: 'logos:facebook',
    google: 'logos:google-icon',
    tripAdvisor: 'cib:tripadvisor',
    twitter: 'fa6-brands:square-x-twitter',
    linkedin: 'skill-icons:linkedin',
    instagram: 'skill-icons:instagram',
    placeholder: 'tabler:social',
    bing: 'logos:bing',
    appleMaps: 'mingcute:apple-fill',
    yelp: 'bi:yelp',
    foursquare: 'simple-icons:foursquare',
    waze: 'simple-icons:waze',
    swarm: 'simple-icons:swarm',
    judysbook: 'mdi:message-outline',
    pinterest: 'logos:pinterest',
    tumblr: 'entypo-social:tumblr-with-circle',
    youtube: 'logos:youtube-icon',
    tiktok: 'logos:tiktok-icon',
    zillow: 'simple-icons:zillow',
    whatsapp: 'logos:whatsapp-icon',
    // Added Aug 2026 — the four platforms _PostCard.const.ts had to spell out inline
    // because they had no entry here.
    threads: 'simple-icons:threads',
    reddit: 'mdi:reddit',
    telegram: 'mdi:telegram',
    bluesky: 'simple-icons:bluesky',
  },

  // EXCEPTION — Phosphor has no alphabetic glyphs. Week / month / year filters.
  letters: {
    m: 'tabler:letter-m',
    w: 'tabler:letter-w',
    y: 'tabler:letter-y',
  },

  // ------------------------------------------------------------------
  // EXCEPTIONS added Aug 2026, when the last raw literals moved out of call sites.
  // These kept their original Iconify names on purpose: the goal was to stop
  // scattering literals, NOT to restyle these surfaces. Swapping any of them for a
  // Phosphor equivalent is a separate visual decision. Like every string here, they
  // resolve over the network via @iconify/react.
  // ------------------------------------------------------------------

  // Monochrome platform glyphs for the legacy composer's platform toggles, which pair
  // each mark with its own brand `color`. Distinct from `publishers` above (full-color
  // marks) — using those here would recolor the whole toggle row.
  platformGlyphs: {
    facebook: 'mdi:facebook',
    instagram: 'mdi:instagram',
    tiktok: 'ic:baseline-tiktok',
    linkedin: 'mdi:linkedin',
    twitter: 'mdi:twitter',
    google: 'mdi:google',
    youtube: 'mdi:youtube',
    pinterest: 'mdi:pinterest',
    threads: 'mdi:at',
    telegram: 'mdi:telegram',
    bluesky: 'ri:bluesky-fill',
    reddit: 'mdi:reddit',
    unknown: 'mdi:help-circle',
  },

  chatbot: {
    chat: 'mdi:chat',
    close: 'mdi:close',
    viewList: 'mdi:view-list',
    refresh: 'mdi:refresh',
  },

  // Competitor radar delta markers. The rounded material arrows read as a matched
  // set with the flat "no change" dash; Phosphor has no equivalent flat marker.
  trend: {
    up: 'material-symbols:arrow-upward-rounded',
    down: 'material-symbols:arrow-downward-rounded',
    flat: 'material-symbols:remove-rounded',
  },

  legacyComposer: {
    arrowRight: 'mdi:arrow-right',
    calendar: 'mdi:calendar',
  },

  settingsDrawer: {
    chevronBack: 'eva:arrow-ios-back-fill',
    chevronForward: 'eva:arrow-ios-forward-fill',
  },

  // Member onboarding v2 (US) choices, keyed by the BE answer code (Figma file MOcFSyXZwM5GzjJZd7ZAMR).
  onboardingCategories: {
    BEAUTY_WELLNESS: HighlighterCircle,
    RETAIL: Handbag,
    FITNESS: Barbell,
    HEALTH_MEDICAL: Stethoscope,
    PROFESSIONAL_SERVICES: Toolbox,
    RESTAURANT_HOSPITALITY: ForkKnife,
    HOME_SERVICES: House,
    PET_SERVICES: Cat,
    AUTOMOTIVE: Car,
    OTHER: Sparkle,
  },

  onboardingSubcategories: {
    HAIR_BARBER: HairDryer,
    BEAUTY_CENTER: HandSoap,
    NAIL_SALON: HighlighterCircle,
    SPA_MASSAGE: FlowerLotus,
    MEDICAL_AESTHETICS: Syringe,
    BROWS_LASHES: Eye,
    TATTOO_PIERCING: PenNib,
    BEAUTY_WELLNESS_OTHER: Sparkle,
    DENTAL: Tooth,
    MEDICAL_CLINIC: Stethoscope,
    PHYSIOTHERAPY: Person,
    MENTAL_HEALTH: Brain,
    EYE_CARE: Eyeglasses,
    PHARMACY: Pill,
    MANUAL_THERAPY: Bone,
    DIAGNOSTICS: Microscope,
    HEALTH_MEDICAL_OTHER: Sparkle,
    PLUMBING: Pipe,
    HEATING_COOLING: Fan,
    ELECTRICAL: PlugCharging,
    CLEANING: Broom,
    LANDSCAPING: PottedPlant,
    CONSTRUCTION_RENOVATION: SolarRoof,
    PEST_CONTROL: BugBeetle,
    PAINTING_MAINTENANCE: PaintBrush,
    HOME_SERVICES_OTHER: Sparkle,
    LEGAL_SERVICES: PenNib,
    ACCOUNTING_TAX: Percent,
    INSURANCE: ShieldCheck,
    REAL_ESTATE: House,
    FINANCIAL_ADVISORY: ChartLineUp,
    ARCHITECTURE_ENGINEERING: Compass,
    MARKETING_CONSULTING: Megaphone,
    PROFESSIONAL_SERVICES_OTHER: Sparkle,
    AUTO_REPAIR: Wrench,
    CAR_WASH_DETAILING: SprayBottle,
    VEHICLE_DEALERSHIP: Handshake,
    TIRE_SHOP: Tire,
    BODY_SHOP: Car,
    TOWING: TruckTrailer,
    AUTOMOTIVE_OTHER: Sparkle,
    VETERINARY: Stethoscope,
    PET_GROOMING: Scissors,
    PET_BOARDING: Hospital,
    PET_SITTING: PawPrint,
    PET_TRAINING: Dog,
    PET_STORE: Storefront,
    PET_SERVICES_OTHER: Sparkle,
    GYM: Barbell,
    YOGA_PILATES: FlowerLotus,
    PERSONAL_TRAINING: SneakerMove,
    MARTIAL_ARTS: BoxingGlove,
    DANCE_STUDIO: DiscoBall,
    CYCLING_STUDIO: Bicycle,
    CLIMBING_GYM: PersonSimpleHike,
    FITNESS_OTHER: Sparkle,
    CLOTHING_FASHION: Dress,
    HOME_GIFTS: Gift,
    JEWELRY_ACCESSORIES: SketchLogo,
    ELECTRONICS: Devices,
    FLORIST: FlowerTulip,
    BOOKS_STATIONERY: Books,
    GROCERY_FOOD: Basket,
    BEAUTY_RETAIL: Drop,
    RETAIL_OTHER: Sparkle,
    RESTAURANT: ForkKnife,
    CAFE: Coffee,
    BAKERY_PASTRY: Cake,
    BAR_NIGHTLIFE: Martini,
    QUICK_SERVICE: Hamburger,
    CATERING_EVENTS: CallBell,
    ACCOMMODATION: Bed,
    STREET_FOOD: Truck,
    RESTAURANT_HOSPITALITY_OTHER: Sparkle,
    OTHER: Sparkle,
  },

  onboardingGoals: {
    SOCIAL_MEDIA_ACTIVE: DeviceMobile,
    LOCAL_DISCOVERY: MapPinArea,
    BUSINESS_INFO_UP_TO_DATE: GlobeSimple,
    MARKETING_ALL_IN_ONE: Files,
  },
};

export default icons;
